/**
 * Page STATISTIQUES (#/stats) : totaux globaux de la CdM en pastilles, puis quatre
 * tables triables (à deux niveaux) et filtrables, du plus AGRÉGÉ au plus GRANULAIRE —
 * GROUPES → ÉQUIPES → MATCHS → JOUEURS (la plus longue table en dernier). Groupes,
 * équipes et matchs sont agrégés EN TEMPS RÉEL depuis l'état officiel (flux SSE),
 * score live inclus et signalé (⏱). Les joueurs viennent de l'agrégat serveur
 * (leaderboards.json), rafraîchi périodiquement. Le tri/filtre survit aux re-rendus
 * (état dans l'instance, écouteurs délégués sur le conteneur).
 */
import type { OfficialState } from "../app/officialStore.ts";
import { computeTournamentStats, type TeamStat, type MatchStat, type GroupStat } from "../app/tournamentStats.ts";
import { fetchLeaderboards, type PlayerStat } from "../app/leaderboards.ts";
import { roundName } from "./html.ts";
import { esc, flagCode, teamName } from "./html.ts";
import { fmtKick } from "../app/format.ts";
import { t } from "../app/i18n.ts";
import { openStatEvolution } from "./statEvolution.ts";
import {
  createSortState,
  cycleSort,
  sortClass,
  sortMark,
  sortAria,
  sortRows,
  type SortState,
} from "./components/tableSort.ts";

interface Col<T, K extends string> {
  readonly key: K;
  readonly label: string;
  readonly title?: string;
  readonly val: (r: T) => number; // clé de tri numérique (0 si non triable)
  readonly cell: (r: T) => string; // contenu HTML de la cellule
  readonly sortable?: boolean;
}

const n0 = (v: number | null): string => (v == null ? "—" : Math.round(v).toString());
const n1 = (v: number | null): string => (v == null ? "—" : v.toFixed(1));
const pct = (v: number | null): string => (v == null ? "—" : `${Math.round(v)}%`);
/** Affiche une stat PAR ÉQUIPE « a–b » (ordre du match) ; « — » si les deux manquent. */
const duo = (a: number | null, b: number | null, f: (n: number) => string): string =>
  a == null && b == null ? "—" : `${a == null ? "–" : f(a)}–${b == null ? "–" : f(b)}`;
const sumOr = (a: number | null, b: number | null): number => (a == null && b == null ? -1 : (a ?? 0) + (b ?? 0));
/** Tirs « cadrés/total » (ex. 5/12), total atténué ; « — » si aucun tir. */
const onOf = (on: number | null, total: number | null): string =>
  total ? `${on ?? 0}<span class="st-pv-sub">/${total}</span>` : "—";
const live = (): string => `<span class="st-live" title="${t("stats.live.title")}">⏱</span>`;
const flag = (c: string): string => `<span class="f fi fi-${esc(flagCode(c))}" aria-hidden="true"></span>`;
const txt = (html: string): string => html.replace(/<[^>]+>/g, "").trim();

/** Clé d'évolution d'une colonne (= la stat à grapher au clic), ou null si la colonne
 *  n'a pas d'évolution pertinente. Mappe les colonnes au facteur le plus cohérent :
 *  match → série intra-match (minute) ; équipe/joueur → match par match ; groupe →
 *  dispatch par nation. Quelques clés de colonne diffèrent de la clé de stat. */
function evKey(table: "group" | "team" | "match" | "player", key: string): string | null {
  if (table === "match") return ({ xg: "xg", shots: "shots", poss: "possession", passAcc: "passAcc" } as Record<string, string>)[key] ?? null;
  if (table === "group") return ["goals", "xg", "shots", "corners", "cards"].includes(key) ? key : null;
  if (table === "team")
    return ["points", "goalsFor", "goalsAgainst", "diff", "xg", "xga", "shots", "possession", "passPct", "corners", "cards", "minutes"].includes(key)
      ? key === "passPct" ? "passAcc" : key
      : null;
  return ["goals", "assists", "shots", "keyPasses", "passAcc", "duelWinPct", "rating", "minutes"].includes(key) ? key : null;
}

// État plié/déplié des rubriques (persisté : survit aux re-rendus live ET au rechargement).
const COLLAPSE_KEY = "wc26.stats.collapsed";
function loadCollapse(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}
function saveCollapse(c: Record<string, boolean>): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(c));
  } catch {
    /* stockage indispo → état de session uniquement */
  }
}

const STAGE_LABEL = (s: string, md: number | null): string =>
  s === "group" ? t("stats.stage.group", { md: md ?? "" }) : roundName(s);

/**
 * % de passes réussies d'une ÉQUIPE = Σ(réussies) / Σ(tentées) sur TOUS ses joueurs,
 * pondéré par le volume (réussies reconstruites depuis passAcc·passes). Vide hors prod
 * (pas de stats joueur). Pur → testé.
 */
export function teamPassCompletion(players: readonly PlayerStat[]): Record<string, { pct: number; total: number }> {
  const acc: Record<string, { ok: number; tot: number }> = {};
  for (const p of players) {
    if (!p.teamCode || p.passAcc == null || !(p.passes > 0)) continue;
    const a = (acc[p.teamCode] ??= { ok: 0, tot: 0 });
    a.ok += (p.passAcc / 100) * p.passes;
    a.tot += p.passes;
  }
  const out: Record<string, { pct: number; total: number }> = {};
  for (const [code, a] of Object.entries(acc)) if (a.tot > 0) out[code] = { pct: (a.ok / a.tot) * 100, total: a.tot };
  return out;
}

export class StatsPage {
  private readonly getState: () => OfficialState | null;
  private players: readonly PlayerStat[] = [];
  private active = false;
  private wired = false;

  private readonly groupSort: SortState<string> = createSortState("group", 1); // A→L par défaut
  private readonly teamSort: SortState<string> = createSortState("points");
  private readonly matchSort: SortState<string> = createSortState("date");
  private readonly playerSort: SortState<string> = createSortState("rating");
  private groupF = ""; // filtre groupe (équipes)
  private stageF = ""; // filtre tour (matchs)
  private search = ""; // recherche joueur
  private collapsed: Record<string, boolean> = loadCollapse(); // rubriques pliées
  private teamPass: Record<string, { pct: number; total: number }> = {}; // %P + volume équipe (agrégé des joueurs)

  private readonly groupCols: ReadonlyArray<Col<GroupStat, string>>;
  private readonly teamCols: ReadonlyArray<Col<TeamStat, string>>;
  private readonly matchCols: ReadonlyArray<Col<MatchStat, string>>;
  private readonly playerCols: ReadonlyArray<Col<PlayerStat, string>>;

  constructor(
    private readonly el: HTMLElement,
    opts: { getState: () => OfficialState | null },
  ) {
    this.getState = opts.getState;
    void fetchLeaderboards().then((lb) => {
      this.players = lb?.playerStats ?? [];
      if (this.active) this.render();
    });

    this.groupCols = [
      { key: "group", label: t("stats.col.group"), title: t("stats.col.group.title"), val: (g) => g.group.charCodeAt(0), cell: (g) => `${g.live ? live() : ""}<b>${esc(t("stats.group.cell", { group: g.group }))}</b>${g.complete ? ` <span class="st-done" title="${t("stats.group.done.title")}">✓</span>` : ""}` },
      { key: "played", label: t("stats.col.played.abbr"), title: t("stats.col.played.group.title"), val: (g) => g.played, cell: (g) => `${g.played}<span class="st-pv-sub">/${g.total}</span>` },
      { key: "goals", label: t("stats.col.goals"), title: t("stats.col.goals.group.title"), val: (g) => g.goals, cell: (g) => `<b>${g.goals}</b>` },
      { key: "gpm", label: t("stats.col.gpm.abbr"), title: t("stats.col.gpm.title"), val: (g) => g.goalsPerMatch ?? -1, cell: (g) => (g.goalsPerMatch == null ? "—" : g.goalsPerMatch.toFixed(2)) },
      { key: "xg", label: t("stats.col.xgHead"), title: t("stats.col.xg.group.title"), val: (g) => g.xg, cell: (g) => n1(g.xg) },
      { key: "shots", label: t("stats.col.shots"), title: t("stats.col.shots.group.title"), val: (g) => g.shots, cell: (g) => String(g.shots) },
      { key: "corners", label: t("stats.col.corners.abbr"), title: t("stats.col.corners.group.title"), val: (g) => g.corners, cell: (g) => String(g.corners) },
      { key: "cards", label: "🟨🟥", title: t("stats.col.cards.group.title"), val: (g) => g.yellow + g.red, cell: (g) => `${g.yellow}/${g.red}` },
    ];

    this.teamCols = [
      { key: "name", label: t("stats.col.team"), title: t("stats.col.team.title"), val: () => 0, sortable: false, cell: (t) => `${t.live ? live() : ""}${flag(t.code)}<span class="st-nm">${esc(teamName(t.code))}</span>` },
      { key: "played", label: t("stats.col.played.abbr"), title: t("stats.col.played.team.title"), val: (t) => t.played, cell: (t) => String(t.played) },
      { key: "minutes", label: t("stats.col.minutes.abbr"), title: t("stats.col.minutes.team.title"), val: (t) => t.minutes, cell: (t) => `${t.minutes}'` },
      { key: "points", label: t("stats.col.points.abbr"), title: t("stats.col.points.title"), val: (t) => t.points, cell: (t) => `<b>${t.points}</b>` },
      { key: "goalsFor", label: t("stats.col.goalsFor.abbr"), title: t("stats.col.goalsFor.title"), val: (t) => t.goalsFor, cell: (t) => String(t.goalsFor) },
      { key: "goalsAgainst", label: t("stats.col.goalsAgainst.abbr"), title: t("stats.col.goalsAgainst.title"), val: (t) => t.goalsAgainst, cell: (t) => String(t.goalsAgainst) },
      { key: "diff", label: t("stats.col.diff.abbr"), title: t("stats.col.diff.title"), val: (t) => t.diff, cell: (t) => (t.diff > 0 ? `+${t.diff}` : String(t.diff)) },
      { key: "xg", label: t("stats.col.xgHead"), title: t("stats.col.xg.team.title"), val: (t) => t.xg, cell: (t) => n1(t.xg) },
      { key: "xga", label: t("stats.col.xgaHead"), title: t("stats.col.xga.title"), val: (t) => t.xga, cell: (t) => n1(t.xga) },
      { key: "shots", label: t("stats.col.shots"), title: t("stats.col.shots.team.title"), val: (t) => t.shots, cell: (t) => onOf(t.shotsOn, t.shots) },
      { key: "possession", label: t("stats.col.possession.abbr"), title: t("stats.col.possession.title"), val: (t) => t.possession ?? -1, cell: (t) => pct(t.possession) },
      { key: "passPct", label: t("stats.col.passPct.abbr"), title: t("stats.col.passPct.team.title"), val: (t) => this.teamPass[t.code]?.pct ?? -1, cell: (t) => { const v = this.teamPass[t.code]; return v ? `${Math.round(v.pct)}%<span class="st-pv-sub">/${v.total}</span>` : "—"; } },
      { key: "corners", label: t("stats.col.corners.abbr"), title: t("stats.col.corners.team.title"), val: (t) => t.corners, cell: (t) => String(t.corners) },
      { key: "cards", label: "🟨🟥", title: t("stats.col.cards.team.title"), val: (t) => t.yellow + t.red, cell: (t) => `${t.yellow}/${t.red}` },
      { key: "elo", label: t("stats.col.elo"), title: t("stats.col.elo.title"), val: (t) => t.elo, cell: (t) => String(t.elo) },
    ];

    this.matchCols = [
      { key: "match", label: t("stats.col.match"), title: t("stats.col.match.title"), val: () => 0, sortable: false, cell: (m) => `${m.live ? live() : ""}${flag(m.home)}<span class="st-nm">${esc(m.home)}</span><span class="st-vs">${m.homeGoals ?? ""}–${m.awayGoals ?? ""}</span><span class="st-nm">${esc(m.away)}</span>${flag(m.away)}` },
      { key: "stage", label: t("stats.col.stage"), title: t("stats.col.stage.title"), val: (m) => (m.stage === "group" ? 0 : 1), cell: (m) => `<span class="st-stage">${esc(STAGE_LABEL(m.stage, m.matchday))}</span>` },
      { key: "goals", label: t("stats.col.goals"), title: t("stats.col.goals.match.title"), val: (m) => m.goals ?? -1, cell: (m) => n0(m.goals) },
      { key: "xg", label: t("stats.col.xgHead"), title: t("stats.col.xg.match.title"), val: (m) => sumOr(m.xgHome, m.xgAway), cell: (m) => duo(m.xgHome, m.xgAway, (n) => n.toFixed(1)) },
      { key: "shots", label: t("stats.col.shots"), title: t("stats.col.shots.match.title"), val: (m) => m.shots ?? -1, cell: (m) => (m.shotsHome == null && m.shotsAway == null ? "—" : `${onOf(m.onTargetHome, m.shotsHome)}–${onOf(m.onTargetAway, m.shotsAway)}`) },
      { key: "poss", label: t("stats.col.poss.abbr"), title: t("stats.col.poss.match.title"), val: (m) => m.possHome ?? -1, cell: (m) => duo(m.possHome, m.possAway, (n) => String(Math.round(n))) },
      { key: "passAcc", label: t("stats.col.passPct.abbr"), title: t("stats.col.passPct.match.title"), val: (m) => (m.passHome != null || m.passAway != null ? ((m.passHome ?? 0) + (m.passAway ?? 0)) / 2 : -1), cell: (m) => duo(m.passHome, m.passAway, (n) => String(Math.round(n))) },
      { key: "cards", label: t("stats.col.cards"), title: t("stats.col.cards.match.title"), val: (m) => m.cards ?? -1, cell: (m) => n0(m.cards) },
      { key: "date", label: t("stats.col.date"), title: t("stats.col.date.title"), val: (m) => Date.parse(m.date) || 0, cell: (m) => `<span class="st-date">${esc(fmtKick(m.date))}</span>` },
    ];

    this.playerCols = [
      { key: "name", label: t("fiche.player.fallbackName"), title: t("stats.col.player.title"), val: () => 0, sortable: false, cell: (p) => `${flag(p.teamCode)}<span class="st-nm">${esc(p.name ?? "—")}</span>` },
      { key: "pos", label: t("stats.col.pos"), title: t("stats.col.pos.title"), val: () => 0, sortable: false, cell: (p) => `<span class="st-pos">${esc(p.pos ?? "")}</span>` },
      { key: "apps", label: t("stats.col.played.abbr"), title: t("stats.col.apps.title"), val: (p) => p.apps, cell: (p) => String(p.apps) },
      { key: "minutes", label: t("stats.col.minutes.abbr"), title: t("stats.col.minutes.player.title"), val: (p) => p.minutes, cell: (p) => String(p.minutes) },
      { key: "goals", label: t("stats.col.goals.abbr"), title: t("stats.col.goals.player.title"), val: (p) => p.goals, cell: (p) => `<b>${p.goals}</b>` },
      { key: "assists", label: t("stats.col.assists.abbr"), title: t("stats.col.assists.title"), val: (p) => p.assists, cell: (p) => String(p.assists) },
      { key: "shots", label: t("stats.col.shots"), title: t("stats.col.shots.player.title"), val: (p) => p.shots, cell: (p) => onOf(p.shotsOn, p.shots) },
      { key: "keyPasses", label: t("stats.col.keyPasses.abbr"), title: t("stats.col.keyPasses.title"), val: (p) => p.keyPasses, cell: (p) => String(p.keyPasses) },
      { key: "passAcc", label: t("stats.col.passPct.abbr"), title: t("stats.col.passPct.player.title"), val: (p) => p.passAcc ?? -1, cell: (p) => (p.passAcc == null ? "—" : `${Math.round(p.passAcc)}%<span class="st-pv-sub">/${p.passes}</span>`) },
      { key: "duelWinPct", label: t("stats.col.duelWinPct.abbr"), title: t("stats.col.duelWinPct.title"), val: (p) => p.duelWinPct ?? -1, cell: (p) => pct(p.duelWinPct == null ? null : p.duelWinPct * 100) },
      { key: "rating", label: t("stats.col.rating"), title: t("stats.col.rating.title"), val: (p) => p.rating ?? -1, cell: (p) => (p.rating == null ? "—" : `<b class="st-rate ${p.rating >= 7.5 ? "hi" : p.rating >= 6.5 ? "mid" : "lo"}">${p.rating.toFixed(2)}</b>`) },
    ];
  }

  activate(): void {
    this.active = true;
    this.render();
  }

  onUpdate(): void {
    if (this.active && !this.el.classList.contains("hide")) this.render();
  }

  private wire(): void {
    if (this.wired) return;
    this.wired = true;
    this.el.addEventListener("click", (e) => {
      // pli/dépli d'une rubrique (anime directement, sans re-rendu ; l'état persiste).
      const tg = (e.target as Element).closest<HTMLElement>(".st-sh-tg[data-toggle]");
      if (tg) {
        const sec = tg.closest<HTMLElement>(".st-sec");
        const key = tg.dataset.toggle ?? "";
        if (sec) {
          sec.classList.toggle("collapsed");
          const isC = sec.classList.contains("collapsed");
          tg.setAttribute("aria-expanded", String(!isC));
          this.collapsed[key] = isC;
          saveCollapse(this.collapsed);
        }
        return;
      }
      // clic sur une CELLULE de stat → modale d'évolution (avant tout : stopPropagation
      // pour ne pas aussi ouvrir la fiche de la ligne via le gestionnaire global).
      const cell = (e.target as Element).closest<HTMLElement>("td[data-ev]");
      if (cell) {
        e.stopPropagation();
        const row = cell.closest<HTMLElement>("tr");
        const state = this.getState();
        const statKey = cell.dataset.ev ?? "";
        const label = cell.dataset.evlabel ?? statKey;
        if (state && row) {
          if (row.dataset.grp) openStatEvolution(state, { kind: "group", group: row.dataset.grp, statKey, label });
          else if (row.dataset.team) openStatEvolution(state, { kind: "team", code: row.dataset.team, statKey, label });
          else if (row.dataset.match) openStatEvolution(state, { kind: "match", id: Number(row.dataset.match), statKey, label });
          else if (row.dataset.player)
            openStatEvolution(state, { kind: "player", id: Number(row.dataset.player), name: row.dataset.name ?? "", statKey, label });
        }
        return;
      }
      const th = (e.target as Element).closest<HTMLElement>("th[data-sort]");
      if (th) {
        const table = th.dataset.table;
        const key = th.dataset.sort ?? "";
        if (table === "group") cycleSort(this.groupSort, key);
        else if (table === "team") cycleSort(this.teamSort, key);
        else if (table === "match") cycleSort(this.matchSort, key);
        else if (table === "player") cycleSort(this.playerSort, key);
        this.render();
        return;
      }
      // clic sur une ligne de groupe → filtre les équipes de ce groupe + défile
      const grpRow = (e.target as Element).closest<HTMLElement>("tr[data-grp]");
      if (grpRow) {
        this.groupF = this.groupF === grpRow.dataset.grp ? "" : (grpRow.dataset.grp ?? "");
        this.collapsed.teams = false; // filtrer un groupe → on déplie les Équipes pour les voir
        this.render();
        this.el.querySelector('[data-sec="teams"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    this.el.addEventListener("input", (e) => {
      const t = e.target as HTMLElement;
      if (t.matches(".st-search")) this.search = (t as HTMLInputElement).value.toLowerCase();
      else if (t.matches(".st-fgroup")) this.groupF = (t as HTMLSelectElement).value;
      else if (t.matches(".st-fstage")) this.stageF = (t as HTMLSelectElement).value;
      else return;
      this.render();
    });
  }

  private tableHtml<T, K extends string>(
    table: "group" | "team" | "match" | "player",
    cols: ReadonlyArray<Col<T, K>>,
    rows: readonly T[],
    s: SortState<K>,
    rowAttrs: (r: T) => string,
  ): string {
    const sorted = sortRows(rows, s, (r, k) => cols.find((c) => c.key === k)?.val(r) ?? 0, () => 0);
    const head = cols
      .map((c) =>
        c.sortable === false
          ? `<th class="${c.title ? "st-help" : ""}"${c.title ? ` title="${esc(c.title)}"` : ""}>${c.label}</th>`
          : `<th data-table="${table}" data-sort="${c.key}" class="st-th ${c.title ? "st-help" : ""} ${sortClass(s, c.key)}" aria-sort="${sortAria(s, c.key)}"${c.title ? ` title="${esc(c.title)}"` : ""}>${c.label}${sortMark(s, c.key)}</th>`,
      )
      .join("");
    const body = sorted
      .map(
        (r) =>
          `<tr ${rowAttrs(r)}>${cols
            .map((c) => {
              const ev = evKey(table, c.key);
              return `<td${ev ? ` class="st-cell-ev" data-ev="${esc(ev)}" data-evlabel="${esc(txt(c.label))}" title="${esc(t("stats.ev.cellHint"))}"` : ""}>${c.cell(r)}</td>`;
            })
            .join("")}</tr>`,
      )
      .join("");
    return `<div class="st-tw"><table class="st-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
  }

  /** Rubrique PLIABLE : en-tête (bouton chevron + titre + compteur) + contrôle (filtre)
   *  + corps animé (grid-rows 1fr↔0fr). L'état plié est persisté et réappliqué au rendu. */
  private section(key: string, title: string, count: number, control: string, body: string): string {
    const collapsed = this.collapsed[key] ?? false;
    return `<section class="st-sec${collapsed ? " collapsed" : ""}" data-sec="${esc(key)}">
      <div class="st-sh">
        <button type="button" class="st-sh-tg" data-toggle="${esc(key)}" aria-expanded="${!collapsed}">
          <i class="ti ti-chevron-down st-chev" aria-hidden="true"></i>
          <h2 class="st-h">${esc(title)} <span class="count">${count}</span></h2>
        </button>
        ${control}
      </div>
      <div class="st-sec-body"><div class="st-sec-inner">${body}</div></div>
    </section>`;
  }

  private render(): void {
    this.wire();
    const state = this.getState();
    if (!state) {
      this.el.innerHTML = `<p class="note st-empty">${t("stats.empty.waiting")}</p>`;
      return;
    }
    const stats = computeTournamentStats(state);
    const T = stats.totals;
    this.teamPass = teamPassCompletion(this.players); // %P équipe (agrégé des joueurs, prod)
    const pill = (v: string, l: string, live = false): string =>
      `<div class="st-pill${live ? " live" : ""}"><span class="st-pv">${v}</span><span class="st-pl">${esc(l)}</span></div>`;

    // filtres
    const groups = [...new Set(stats.teams.map((t) => t.group).filter(Boolean))].sort();
    const stages = [...new Set(stats.matches.map((m) => m.stage))];
    const teams = this.groupF ? stats.teams.filter((t) => t.group === this.groupF) : stats.teams;
    const matches = this.stageF ? stats.matches.filter((m) => m.stage === this.stageF) : stats.matches;
    const liveTeams = new Set(stats.teams.filter((t) => t.live).map((t) => t.code));
    const players = this.search
      ? this.players.filter((p) => (p.name ?? "").toLowerCase().includes(this.search) || p.teamCode.toLowerCase().includes(this.search))
      : this.players;

    const groupOpts = `<option value="">${t("stats.filter.allGroups")}</option>${groups.map((g) => `<option value="${g}"${g === this.groupF ? " selected" : ""}>${t("stats.filter.group.option", { g })}</option>`).join("")}`;
    const stageOpts = `<option value="">${t("stats.filter.allStages")}</option>${stages.map((s) => `<option value="${s}"${s === this.stageF ? " selected" : ""}>${esc(s === "group" ? t("stats.filter.groupStage") : roundName(s))}</option>`).join("")}`;

    this.el.innerHTML = `
      <div class="st-page">
        <h1 class="st-title">${t("stats.title")}${T.liveNow ? ` <span class="st-livenow">${t("stats.liveNow", { n: T.liveNow })}</span>` : ""}</h1>
        <div class="st-pills">
          ${pill(`${T.matchesPlayed}<span class="st-pv-sub">/${T.matchesTotal}</span>`, t("stats.col.played.team.title"))}
          ${pill(String(T.goals), t("common.goals"))}
          ${pill(T.goalsPerMatch != null ? T.goalsPerMatch.toFixed(2) : "—", t("stats.pill.goalsPerMatch"))}
          ${pill(n1(T.xg), t("stats.pill.xgTotal"))}
          ${pill(String(T.shots), t("stats.pill.shots"))}
          ${pill(String(T.corners), t("stats.pill.corners"))}
          ${pill(String(T.yellow), t("stats.pill.yellowCards"))}
          ${pill(String(T.red), t("stats.pill.redCards"))}
        </div>

        ${this.section(
          "teams",
          t("stats.section.teams"),
          teams.length,
          `<select class="st-fgroup" aria-label="${t("stats.filter.byGroup.aria")}">${groupOpts}</select>`,
          this.tableHtml("team", this.teamCols, teams, this.teamSort, (t) => `data-team="${esc(t.code)}" tabindex="0" role="button"`),
        )}

        ${
          stats.groups.length
            ? this.section(
                "groups",
                t("stats.section.groups"),
                stats.groups.length,
                `<span class="st-hint">${t("stats.section.groups.hint")}</span>`,
                this.tableHtml("group", this.groupCols, stats.groups, this.groupSort, (g) => `data-grp="${esc(g.group)}" class="st-grow${g.group === this.groupF ? " on" : ""}" tabindex="0" role="button" title="${esc(t("stats.group.row.title", { group: g.group }))}"`),
              )
            : ""
        }

        ${this.section(
          "matches",
          t("stats.section.matches"),
          matches.length,
          `<select class="st-fstage" aria-label="${t("stats.filter.byStage.aria")}">${stageOpts}</select>`,
          this.tableHtml("match", this.matchCols, matches, this.matchSort, (m) => `data-match="${m.id}" tabindex="0" role="button"`),
        )}

        ${this.section(
          "players",
          t("stats.section.players"),
          players.length,
          `<input class="st-search" type="search" placeholder="${t("stats.search.placeholder")}" value="${esc(this.search)}" aria-label="${t("stats.search.placeholder")}">`,
          this.players.length ? this.tableHtml("player", this.playerCols, players, this.playerSort, (p) => `data-player="${p.id}" data-name="${esc(p.name ?? "")}"${liveTeams.has(p.teamCode) ? ' data-live="1"' : ""} tabindex="0" role="button"`) : `<p class="note st-empty">${t("stats.players.empty")}</p>`,
        )}
        <p class="note st-foot">${t("stats.footerNote")}<a href="#/method">${t("stats.footer.method.link")}</a>.</p>
      </div>`;
  }
}
